import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatNumber } from '../../utils/formatters.js';
import { countBy, sumBy } from '../../utils/insightStats.js';

function severityOf(row) {
  const onHand = Number(row.quantity);
  const reorder = Number(row.reorderPoint);
  if (onHand <= 0) return 'OUT';
  if (onHand <= reorder * 0.5) return 'CRITICAL';
  return 'LOW';
}

const SEVERITY_BADGE_VARIANT = { OUT: 'danger', CRITICAL: 'danger', LOW: 'warning' };

export function LowStockPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['inventory', 'low-stock'],
    queryFn: () => inventoryApi.lowStock().then((r) => r.items || []),
  });

  const palette = useChartPalette();
  const rows = data || [];
  const stats = useMemo(() => {
    const counts = countBy(rows, severityOf);
    return {
      out: counts.OUT || 0,
      critical: counts.CRITICAL || 0,
      low: counts.LOW || 0,
      shortfall: sumBy(rows, (r) => Math.max(0, Number(r.reorderPoint || 0) - Number(r.quantity || 0))),
    };
  }, [rows]);

  const severityLabel = {
    OUT: t('inv.lowStock.sev.out'),
    CRITICAL: t('inv.lowStock.sev.critical'),
    LOW: t('inv.lowStock.sev.low'),
  };

  const columns = [
    { key: 'sku', label: t('inv.products.col.sku') },
    { key: 'productName', label: t('col.product') },
    { key: 'warehouseCode', label: t('col.warehouse') },
    { key: 'quantity',     label: t('inv.lowStock.col.onHand'),  align: 'right', render: (r) => formatNumber(r.quantity) },
    { key: 'reorderPoint', label: t('inv.products.col.reorder'), align: 'right', render: (r) => formatNumber(r.reorderPoint) },
    {
      key: 'severity',
      label: t('inv.lowStock.col.severity'),
      render: (r) => {
        const severity = severityOf(r);
        return <Badge variant={SEVERITY_BADGE_VARIANT[severity]}>{severityLabel[severity]}</Badge>;
      },
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('inv.lowStock.title')}</h1>
          <p className="muted">{t('inv.lowStock.subtitle')}</p>
        </div>
      </header>
      {rows.length > 0 && (
        <InsightBar
          title={t('insight.bySeverity')}
          labels={[severityLabel.OUT, severityLabel.CRITICAL, severityLabel.LOW]}
          values={[stats.out, stats.critical, stats.low]}
          colors={[palette?.expense, palette?.series?.[3], palette?.series?.[2]]}
          metrics={[
            { label: t('inv.lowStock.insight.affected'), value: rows.length },
            { label: t('inv.lowStock.insight.outOfStock'), value: stats.out },
            { label: severityLabel.CRITICAL, value: stats.critical },
            { label: t('inv.lowStock.insight.shortfall'), value: formatNumber(stats.shortfall) },
          ]}
        />
      )}
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} emptyMessage={t('empty.lowStock')} />
    </section>
  );
}
